import type { ReactNode } from 'react'
import { useLayoutEffect, useRef } from 'react'

/**
 * Antetul paginii, care ramane pe ecran cand se deruleaza.
 *
 * Solicitare: „pune un freeze la sectiunea de sus, in toate tab-urile unde se justifica".
 *
 * Pe tabelele lungi — sell-out, rambursuri, incasari — titlul, filtrele si barul de taburi
 * plecau de pe ecran dupa primul ecran de derulare. Ca sa schimbi tabul sau filtrul te
 * intorceai sus, si pierdeai locul din lista.
 *
 * Ce se blocheaza e antetul paginii, nu si capul tabelului: capetele de coloana care au deja
 * `sticky` stau in propriul container cu derulare (`max-h-… overflow-auto`), deci nu se bat cu
 * asta. Unde tabelul nu are container propriu, capul lui deruleaza mai departe — o treaba
 * separata, care cere sa stim inaltimea antetului.
 *
 * `sticky` se raporteaza la containerul care deruleaza — aici `<main>` din AppShell, care are
 * `overflow-auto` si `p-4 md:p-6`. De aceea marginile negative: blocul iese peste captuseala
 * aceea si acopera latimea intreaga, altfel s-ar vedea randuri trecand pe la margini. Fundalul
 * TREBUIE sa fie opac si sa fie chiar culoarea invelisului (`b4l-app`, ca in AppShell), altfel
 * se vede prin el — si in tema intunecata.
 *
 * `top` e NEGATIV, egal cu captuseala lui `<main>` (`-top-4 md:-top-6`). Un element `sticky` se
 * opreste la marginea CONTINUTULUI containerului care deruleaza, nu la marginea lui: cu
 * `top-0` ramanea la 16/24px sub bara aplicatiei, iar prin fasia aceea se vedeau randurile
 * derulate (bug Flota Rompetrol, 04.11.2026). Marginea negativa de sus il trage in repaus peste
 * captuseala, `top` negativ il tine lipit de bara cand se lipeste.
 *
 * Pagina cu antet fix NU pune captuseala proprie (`p-4`) pe radacina: s-ar aduna cu cea din
 * `<main>`, iar marginile negative ar anula doar una dintre ele.
 *
 * Isi publica inaltimea in `--b4l-antet`, ca o zona de sub el sa se poata aseza exact sub
 * antet — de exemplu matricea sell-in/sell-out, care umple ecranul si deruleaza pe dinauntru,
 * ca sa-si tina si filtrele, si capul de tabel pe ecran. Inaltimea nu e constanta: bara se
 * rupe pe doua randuri pe ecrane inguste, deci se masoara, nu se presupune.
 */
export function AntetFix({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const scrie = () => {
      document.documentElement.style.setProperty('--b4l-antet', `${el.offsetHeight}px`)
    }
    scrie()
    const obs = new ResizeObserver(scrie)
    obs.observe(el)
    return () => {
      obs.disconnect()
      // La demontare antetul dispare, deci si inaltimea lui: altfel pagina urmatoare ar
      // pastra un decalaj care nu mai corespunde nimanui.
      document.documentElement.style.removeProperty('--b4l-antet')
    }
  }, [])

  return (
    <div
      ref={ref}
      className="sticky -top-4 z-20 -mx-4 -mt-4 space-y-3 border-b border-b4l-border bg-b4l-app px-4 pb-3 pt-4 md:-top-6 md:-mx-6 md:-mt-6 md:px-6 md:pt-6"
    >
      {children}
    </div>
  )
}
